import { Box, MenuItem } from '@mui/material';
import {
  DialogContainer,
  DialogTitle,
  DialogBody,
  DialogActions,
  CustomTextField,
  CustomSelect,
  CustomTextArea,
  HorizontalForm,
} from '../../../../common/components';
import { getLotLabel } from '../utils/lotTicketsUtils';

const LotTicketDialog = ({
  open,
  onClose,
  editMode,
  selectedLotTicket,
  errors,
  onInputChange,
  onSelectChange,
  onSave,
  availableLots,
  districts,
  countries,
  provinces,
  departments,
  maritalStatuses,
  nationalities,
  allotments,
  canEditLots = true,
}) => {
  const allotmentMap = new Map((allotments || []).map((allotment) => [allotment.uuid, allotment]));

  const getFilteredProvinces = () => {
    return (provinces || []).filter((prov) => prov.uuid_country === selectedLotTicket.uuid_country);
  };

  const getFilteredDepartments = () => {
    return (departments || []).filter(
      (dep) => dep.uuid_province === selectedLotTicket.uuid_province
    );
  };

  const getFilteredDistricts = () => {
    return (districts || []).filter(
      (dist) => dist.uuid_department === selectedLotTicket.uuid_department
    );
  };

  return (
    <DialogContainer open={open} onClose={onClose} size='small'>
      <DialogTitle>{editMode ? 'Editar Boleto' : 'Agregar Boleto'}</DialogTitle>

      <DialogBody>
        <DialogTitle showSeparator={true} sx={{ p: 0 }}>
          Datos personales
        </DialogTitle>
        <HorizontalForm withBorder={false} sx={{ p: 1 }}>
          <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 2, mr: 4 }}>
            <CustomTextField
              label="Nombre completo"
              name="full_name"
              value={selectedLotTicket?.full_name || ''}
              onChange={onInputChange}
              placeholder="Ej: Juan Perez"
              error={Boolean(errors?.full_name)}
              helperText={errors?.full_name}
            />
            <CustomTextField
              label="Número de boleto"
              name="ticket_number"
              value={selectedLotTicket?.ticket_number || ''}
              onChange={onInputChange}
              placeholder="Ej: B-1001"
              error={Boolean(errors?.ticket_number)}
              helperText={errors?.ticket_number}
            />
            <CustomTextField
              label="DNI"
              name="dni"
              value={selectedLotTicket?.dni || ''}
              onChange={onInputChange}
              placeholder="Ej: 12345678"
              error={Boolean(errors?.dni)}
              helperText={errors?.dni}
            />
            <CustomTextField
              label="Fecha de nacimiento"
              name="birth_date"
              type="date"
              value={selectedLotTicket?.birth_date || ''}
              onChange={onInputChange}
              InputLabelProps={{ shrink: true }}
              error={Boolean(errors?.birth_date)}
              helperText={errors?.birth_date}
            />
            <CustomSelect
              label="Género"
              name="gender"
              value={selectedLotTicket?.gender || ''}
              onChange={onSelectChange}
              error={Boolean(errors?.gender)}
              helperText={errors?.gender}
            >
              <MenuItem value="h">Masculino</MenuItem>
              <MenuItem value="m">Femenino</MenuItem>
              <MenuItem value="O">Otro</MenuItem>
            </CustomSelect>
          </Box>
          <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 2 }}>
            <CustomSelect
              label="Estado civil"
              name="uuid_marital_status"
              value={selectedLotTicket?.uuid_marital_status || ''}
              onChange={onSelectChange}
              error={Boolean(errors?.uuid_marital_status)}
              helperText={errors?.uuid_marital_status}
            >
              {(maritalStatuses || []).map((status) => (
                <MenuItem key={status.uuid} value={status.uuid}>
                  {status.description}
                </MenuItem>
              ))}
            </CustomSelect>
            <CustomSelect
              label="Nacionalidad"
              name="uuid_nationality"
              value={selectedLotTicket?.uuid_nationality || ''}
              onChange={onSelectChange}
              error={Boolean(errors?.uuid_nationality)}
              helperText={errors?.uuid_nationality}
            >
              {(nationalities || []).map((nationality) => (
                <MenuItem key={nationality.uuid} value={nationality.uuid}>
                  {nationality.description}
                </MenuItem>
              ))}
            </CustomSelect>
          </Box>
        </HorizontalForm>

        <DialogTitle showSeparator={true} sx={{ p: 0 }}>
          Contacto
        </DialogTitle>
        <HorizontalForm withBorder={false} sx={{ p: 1 }}>
          <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 2, mr: 4 }}>
            <CustomTextField
              label="Dirección"
              name="address"
              value={selectedLotTicket?.address || ''}
              onChange={onInputChange}
              placeholder="Ej: Calle Ejemplo 123"
              error={Boolean(errors?.address)}
              helperText={errors?.address}
            />
            <CustomTextField
              label="Email"
              name="email"
              value={selectedLotTicket?.email || ''}
              onChange={onInputChange}
              placeholder="juan@email.com"
            />
          </Box>
          <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 2 }}>
            <CustomTextField
              label="Teléfono"
              name="phone_number"
              value={selectedLotTicket?.phone_number || ''}
              onChange={onInputChange}
              placeholder="+549..."
            />
            <CustomTextField
              label="Código postal"
              name="postal_code"
              value={selectedLotTicket?.postal_code || ''}
              onChange={onInputChange}
              error={Boolean(errors?.postal_code)}
              helperText={errors?.postal_code}
            />
          </Box>
        </HorizontalForm>

        <DialogTitle showSeparator={true} sx={{ p: 0 }}>
          Ubicación
        </DialogTitle>
        <HorizontalForm withBorder={false} sx={{ p: 1 }}>
          <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 2, mr: 4 }}>
            <CustomSelect
              label="País"
              name="uuid_country"
              value={selectedLotTicket?.uuid_country || ''}
              onChange={onSelectChange}
              error={Boolean(errors?.uuid_country)}
              helperText={errors?.uuid_country}
            >
              {(countries || []).map((country) => (
                <MenuItem key={country.uuid} value={country.uuid}>
                  {country.name}
                </MenuItem>
              ))}
            </CustomSelect>
            <CustomSelect
              label="Provincia"
              name="uuid_province"
              value={selectedLotTicket?.uuid_province || ''}
              onChange={onSelectChange}
              disabled={!selectedLotTicket?.uuid_country}
              error={Boolean(errors?.uuid_province)}
              helperText={errors?.uuid_province}
            >
              {getFilteredProvinces().map((prov) => (
                <MenuItem key={prov.uuid} value={prov.uuid}>
                  {prov.name}
                </MenuItem>
              ))}
            </CustomSelect>
          </Box>
          <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 2 }}>
            <CustomSelect
              label="Departamento"
              name="uuid_department"
              value={selectedLotTicket?.uuid_department || ''}
              onChange={onSelectChange}
              disabled={!selectedLotTicket?.uuid_province}
              error={Boolean(errors?.uuid_department)}
              helperText={errors?.uuid_department}
            >
              {getFilteredDepartments().map((dep) => (
                <MenuItem key={dep.uuid} value={dep.uuid}>
                  {dep.name}
                </MenuItem>
              ))}
            </CustomSelect>
            <CustomSelect
              label="Distrito"
              name="uuid_district"
              value={selectedLotTicket?.uuid_district || ''}
              onChange={onSelectChange}
              disabled={!selectedLotTicket?.uuid_department}
              error={Boolean(errors?.uuid_district)}
              helperText={errors?.uuid_district}
            >
              {getFilteredDistricts().map((district) => (
                <MenuItem key={district.uuid} value={district.uuid}>
                  {district.name}
                </MenuItem>
              ))}
            </CustomSelect>
          </Box>
        </HorizontalForm>

        <DialogTitle showSeparator={true} sx={{ p: 0 }}>
          Boleto
        </DialogTitle>
        <HorizontalForm withBorder={false} sx={{ p: 1 }}>
          <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 2, mr: 4 }}>
            <CustomSelect
              label="Lote"
              name="uuid_lot"
              value={selectedLotTicket?.uuid_lot || ''}
              onChange={onInputChange}
              displayEmpty
            >
              <MenuItem value="">Sin lote</MenuItem>
              {(availableLots || []).length === 0 ? (
                <MenuItem disabled value="no-lots">
                  Sin lotes disponibles
                </MenuItem>
              ) : (
                (availableLots || []).map((lot) => {
                  const allotment = allotmentMap.get(lot.uuid_allotment);
                  return (
                    <MenuItem key={lot.uuid} value={lot.uuid}>
                      {getLotLabel(lot, allotment?.name || '')}
                    </MenuItem>
                  );
                })
              )}
            </CustomSelect>
          </Box>
          <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 2 }}>
            <CustomTextArea
              label="Detalles"
              name="details"
              value={selectedLotTicket?.details || ''}
              onChange={onInputChange}
              placeholder="Observaciones"
              multiline
              minRows={3}
              error={Boolean(errors?.details)}
              helperText={errors?.details}
            />
          </Box>
        </HorizontalForm>
      </DialogBody>

      <DialogActions
        handleClose={onClose}
        handleSave={onSave}
        saveButtonProps={{ disabled: !canEditLots }}
      />
    </DialogContainer>
  );
};

export default LotTicketDialog;
